/**
 * The electronic receipt — on screen, and on paper.
 *
 * Everything printed here comes from the saved receipt, never from the form
 * that issued it: the number and barcode serial the server minted, the time
 * the server stamped, and the signature the server read from the issuing
 * account. What the desk sees is exactly what the patient takes home.
 *
 * The QA report (§4.1) asked for two things the paper lacked: the employee's
 * signature, clearly, and the amount paid — printed on every receipt, zero
 * included. Both are now their own blocks. §5.1 added the barcode: the serial
 * the desk scans to open the patient's file again.
 *
 * The form (§2.1, §2.2, §4.3) builds the receipt from dropdowns, not typing:
 * each line is a service type — كشف, تحاليل, أشعة, صيدلية or anything else —
 * and a checkup picks its clinic, doctor and exam type from the hospital's own
 * list, with the clinic's price filled in. Issuing sends the patient to each
 * line's department (routes/receipts.js), so clinics reception sees them at
 * once.
 */

import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Barcode, Button, ErrorState, Field, Icon, Input, Modal, Select, Spinner } from '../../components/common';
import { receiptsApi } from '../../api/endpoints';
import { usePermissions } from '../../context/PermissionContext';
import { useNotifications } from '../../context/NotificationContext';
import { QueueTicketSlip } from '../queue/QueueTicketSlip';
import { formatDmy, ltr } from '../../domain/bidi';
import { logoPlateClass } from '../../domain/logoBackdrop';
import { receiptShowsMoney, receiptTypeOf } from '../../domain/receipts';
import { DEPARTMENT_META, formatDateTime, formatMoney, printArea } from './labels';

// The deferred title is the specification's wording — an authorisation to
// collect, not a receipt for money taken. Kept in step with Backend/lib/receipts.js.
const DEFAULT_TITLE = 'إذن تحصيل أجل';
const TITLE_FOR_TYPE = { cash: 'إيصال استلام نقدية', deferred: DEFAULT_TITLE };

const STATUS = {
  issued: { label: 'آجل — لم يُحصَّل بالكامل', className: 'border-amber-500/40 bg-amber-500/15 text-amber-100' },
  collected: { label: 'تم التحصيل', className: 'border-emerald-500/40 bg-emerald-500/15 text-emerald-100' },
  void: { label: 'ملغي', className: 'border-slate-500/40 bg-slate-500/15 text-slate-300' },
};

const METHOD_LABELS = { cash: 'نقداً', card: 'بطاقة', wallet: 'محفظة إلكترونية', transfer: 'تحويل' };

const pad = (n) => String(n).padStart(2, '0');
const clock = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

/** "كشف — عيادة الباطنة — د. أحمد" for a routed line, on paper. */
function routeOf(item) {
  if (!item.department) return '—';
  const dept = DEPARTMENT_META[item.department]?.label || item.department;
  return [dept, item.clinicName, item.doctorName].filter(Boolean).join(' — ');
}

/* ========================================================================== */
/* The paper                                                                  */
/* ========================================================================== */

export const ReceiptSheet = forwardRef(function ReceiptSheet({ receipt, children }, ref) {
  const { institutionName, institutionLogo, institutionLogoBackdrop } = usePermissions();
  const p = receipt.patient || {};
  const issued = new Date(receipt.issuedAt || receipt.createdAt);
  const status = STATUS[receipt.status] || STATUS.issued;
  const total = Number(receipt.total || 0);
  const paid = Number(receipt.paidAmount ?? (receipt.status === 'collected' ? total : 0));
  const remaining = Math.max(0, Math.round((total - paid) * 100) / 100);
  const type = receiptTypeOf(receipt);
  // A deferred receipt carries no figure at all (domain/receipts.js).
  const showsMoney = receiptShowsMoney(receipt);
  const routed = (receipt.items || []).some((item) => item.department);

  return (
    <div ref={ref} className="print-sheet receipt-sheet" dir="rtl" data-receipt-number={receipt.number}>
      <div className="receipt-sheet__head">
        <div className="flex min-w-0 items-center gap-3">
          {institutionLogo && <img src={institutionLogo} alt="" className={`receipt-sheet__logo ${logoPlateClass(institutionLogoBackdrop)}`} />}
          <div className="min-w-0">
            <h2 className="text-base font-bold">{institutionName || 'المنشأة الطبية'}</h2>
            <h1 className="flex flex-wrap items-center gap-2 text-xl font-bold">
              {receipt.title || DEFAULT_TITLE}
              <span className={`receipt-type receipt-type--${type}`}>{type === 'cash' ? 'نقدي' : 'آجل'}</span>
            </h1>
          </div>
        </div>
        <div className="flex flex-col items-center gap-1 text-center">
          {receipt.serial && <Barcode value={receipt.serial} height={46} module={1.4} />}
          <p className="font-mono text-sm font-bold" dir="ltr">
            {receipt.number}
          </p>
          <p className="muted text-xs">
            {ltr(formatDmy(issued))} — {ltr(clock(issued))}
          </p>
        </div>
      </div>

      <table className="mt-3">
        <tbody>
          <tr>
            <th style={{ width: '26%' }}>اسم المريض</th>
            <td>{p.fullName || '—'}</td>
          </tr>
          {p.affiliation && (
            <tr>
              <th>جهة التعاقد</th>
              <td>{p.affiliation}</td>
            </tr>
          )}
          {(p.clinic || p.doctor) && (
            <tr>
              <th>العيادة / الطبيب</th>
              <td>{[p.clinic, p.doctor].filter(Boolean).join(' — ')}</td>
            </tr>
          )}
          {(p.phone1 || p.phone2) && (
            <tr>
              <th>أرقام التواصل</th>
              <td>
                <span dir="ltr">{[p.phone1, p.phone2].filter(Boolean).join('  /  ')}</span>
                <div className="muted text-[11px]">أرقام التواصل المباشرة الخاصة بالمريض نفسه</div>
              </td>
            </tr>
          )}
          {p.nationalId && (
            <tr>
              <th>الرقم القومي</th>
              <td dir="ltr" className="text-right">
                {p.nationalId}
              </td>
            </tr>
          )}
          {p.insuranceCode && (
            <tr>
              <th>كود التأمين</th>
              <td dir="ltr" className="text-right">
                {p.insuranceCode}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <table className="mt-3">
        <thead>
          <tr>
            <th style={{ width: '7%' }}>#</th>
            <th>الخدمة</th>
            {routed && <th style={{ width: '32%' }}>التوجيه</th>}
            {showsMoney && <th style={{ width: '20%' }}>المبلغ</th>}
          </tr>
        </thead>
        <tbody>
          {(receipt.items || []).map((item, i) => (
            <tr key={i}>
              <td>{i + 1}</td>
              <td>{item.label}</td>
              {routed && <td>{routeOf(item)}</td>}
              {showsMoney && <td>{formatMoney(item.amount)}</td>}
            </tr>
          ))}
        </tbody>
      </table>

      {/* The money, every line of it — the paid amount is printed even at zero.
          A deferred receipt prints none of it. */}
      {showsMoney && (
        <div className="receipt-sheet__totals">
          <div>
            <span>الإجمالي</span>
            <b>{formatMoney(total)}</b>
          </div>
          <div className="receipt-sheet__paid">
            <span>المبلغ المدفوع</span>
            <b>{formatMoney(paid)}</b>
          </div>
          <div className={remaining > 0 ? 'receipt-sheet__due' : ''}>
            <span>المتبقي</span>
            <b>{formatMoney(remaining)}</b>
          </div>
          <div>
            <span>طريقة الدفع</span>
            <b>{paid > 0 ? METHOD_LABELS[receipt.paymentMethod] || 'نقداً' : '—'}</b>
          </div>
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-stretch justify-between gap-4">
        <div className="min-w-[10rem] flex-1">
          <p className="text-sm">
            <b>الحالة:</b> {status.label}
          </p>
          {receipt.collectedAt && receipt.status === 'collected' && (
            <p className="muted text-xs">تاريخ التحصيل: {formatDateTime(receipt.collectedAt)}</p>
          )}
          {routed && <p className="muted mt-1 text-xs">تم توجيه المريض إلى الأقسام المذكورة أعلاه.</p>}
          {children}
        </div>

        {/* The signature: whose, which job, when — drawn large enough to read
            at arm's length, with a line for the stamp beside it. */}
        <div className="receipt-signature">
          <p className="receipt-signature__label">توقيع الموظف</p>
          <p className="receipt-signature__name">{receipt.signatureName}</p>
          {receipt.signatureTitle && <p className="receipt-signature__title">{receipt.signatureTitle}</p>}
          <div className="receipt-signature__line" />
          <p className="receipt-signature__time">
            توقيع إلكتروني — {ltr(formatDmy(issued))} {ltr(clock(issued))}
          </p>
        </div>
      </div>

      {/* The queue turns this receipt took (QA report §5.3), torn off and kept. */}
      {(receipt.tickets || []).map((ticket) => (
        <QueueTicketSlip key={ticket.id} ticket={ticket} hospitalName={institutionName} />
      ))}
    </div>
  );
});

/** A saved receipt with its print button. */
export function ReceiptPreview({ receipt, extra = null }) {
  const sheet = useRef(null);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Badge className={(STATUS[receipt.status] || STATUS.issued).className}>{(STATUS[receipt.status] || STATUS.issued).label}</Badge>
        <Button icon="printer" onClick={() => printArea(sheet.current, { title: receipt.number || 'إيصال' })}>
          طباعة الإيصال
        </Button>
      </div>
      <ReceiptSheet ref={sheet} receipt={receipt}>
        {extra}
      </ReceiptSheet>
    </div>
  );
}

/* ========================================================================== */
/* The form                                                                   */
/* ========================================================================== */

/** The options, fetched once per page load — the same list for every receipt. */
let optionsCache = null;
function useReceiptOptions() {
  const [state, setState] = useState({ data: optionsCache, error: null });
  const load = useCallback(() => {
    setState({ data: optionsCache, error: null });
    receiptsApi
      .options()
      .then((data) => {
        optionsCache = data;
        setState({ data, error: null });
      })
      .catch((error) => setState({ data: optionsCache, error }));
  }, []);
  useEffect(() => {
    if (!optionsCache) load();
  }, [load]);
  return { ...state, reload: load };
}

export const LINE_KINDS = [
  { id: 'checkup', label: 'كشف', icon: 'stethoscope', tone: 'cyan' },
  { id: 'lab', label: 'تحاليل', icon: 'flask-conical', tone: 'teal' },
  { id: 'radiology', label: 'أشعة', icon: 'scan-line', tone: 'purple' },
  { id: 'pharmacy', label: 'صيدلية', icon: 'pill', tone: 'amber' },
  { id: 'other', label: 'خدمة أخرى', icon: 'receipt', tone: 'slate' },
];

let lineKey = 0;
export const blankLine = (kind = 'checkup', extra = {}) => ({
  key: `l${(lineKey += 1)}`,
  kind,
  doctorId: '',
  clinicId: '',
  examType: 'new',
  serviceId: '',
  label: '',
  amount: '',
  labelTouched: false,
  amountTouched: false,
  ...extra,
});

/** Price and label a checkup line from the clinic and exam type. */
function checkupDefaults(line, options) {
  const doctor = options?.doctors?.find((d) => String(d.id) === String(line.doctorId));
  const clinic = doctor?.clinics?.find((c) => String(c.id) === String(line.clinicId));
  const exam = options?.examTypes?.find((e) => e.id === line.examType);
  const price = clinic?.[exam?.priceField] ?? (line.examType === 'followup' ? doctor?.priceFollowup : doctor?.priceExam);
  const label = [exam?.label || 'كشف', doctor ? `د. ${doctor.name.replace(/^د\.\s*/, '')}` : null, clinic?.name].filter(Boolean).join(' — ');
  return { price: price ?? '', label };
}

function LineEditor({ line, options, onChange, onRemove, removable, hasPatient }) {
  const set = (patch) => onChange({ ...line, ...patch });
  const doctors = options?.doctors || [];
  const clinicsAll = doctors.flatMap((d) => d.clinics.map((c) => ({ ...c, doctorId: d.id, doctorName: d.name })));
  const catalog = (options?.services || []).filter((s) => s.department === line.kind);

  /** Re-derive the label and price when the choice changes, unless typed over. */
  const apply = (next) => {
    let patch = { ...next };
    const merged = { ...line, ...next };
    if (merged.kind === 'checkup') {
      const { price, label } = checkupDefaults(merged, options);
      if (!merged.labelTouched) patch.label = label;
      if (!merged.amountTouched && price !== '' && price !== null) patch.amount = String(price);
    } else if (next.serviceId !== undefined) {
      const service = catalog.find((s) => String(s.id) === String(next.serviceId));
      if (service) {
        if (!merged.labelTouched) patch.label = service.name;
        if (!merged.amountTouched) patch.amount = String(service.price);
      }
    }
    set(patch);
  };

  const kind = LINE_KINDS.find((k) => k.id === line.kind) || LINE_KINDS[0];
  const routedWithoutFile = line.kind !== 'other' && !hasPatient;

  return (
    <div className={`receipt-line tone-${kind.tone}`}>
      <div className="flex flex-wrap items-center gap-1.5">
        {LINE_KINDS.map((k) => (
          <button
            key={k.id}
            type="button"
            aria-pressed={line.kind === k.id}
            onClick={() => apply({ kind: k.id, serviceId: '', labelTouched: false, amountTouched: false, label: '', amount: '' })}
            className={`kind-chip ${line.kind === k.id ? 'kind-chip--on' : ''}`}
          >
            <Icon name={k.icon} size={14} /> {k.label}
          </button>
        ))}
        {removable && (
          <button type="button" className="ms-auto rounded-lg p-1.5 text-slate-400 hover:bg-red-500/10 hover:text-red-300" onClick={onRemove} aria-label="حذف البند">
            <Icon name="trash-2" size={16} />
          </button>
        )}
      </div>

      {line.kind === 'checkup' && (
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Field label="العيادة" required>
            <Select
              value={line.clinicId}
              onChange={(e) => {
                const clinic = clinicsAll.find((c) => String(c.id) === e.target.value);
                apply({ clinicId: e.target.value, doctorId: clinic ? String(clinic.doctorId) : line.doctorId });
              }}
            >
              <option value="">اختر العيادة…</option>
              {clinicsAll.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} — {c.doctorName}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="اسم الطبيب" required>
            <Select
              value={line.doctorId}
              onChange={(e) => {
                const doctor = doctors.find((d) => String(d.id) === e.target.value);
                const keep = doctor?.clinics.some((c) => String(c.id) === String(line.clinicId));
                apply({ doctorId: e.target.value, clinicId: keep ? line.clinicId : String(doctor?.clinics[0]?.id || '') });
              }}
            >
              <option value="">اختر الطبيب…</option>
              {doctors.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                  {d.title ? ` — ${d.title}` : ''}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="نوع الكشف">
            <Select value={line.examType} onChange={(e) => apply({ examType: e.target.value })}>
              {(options?.examTypes || []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      )}

      {(line.kind === 'lab' || line.kind === 'radiology') && catalog.length > 0 && (
        <Field label="الخدمة من قائمة المنشأة" className="mt-3">
          <Select value={line.serviceId} onChange={(e) => apply({ serviceId: e.target.value })}>
            <option value="">اختر أو اكتب الخدمة أدناه…</option>
            {catalog.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} — {formatMoney(s.price)}
              </option>
            ))}
          </Select>
        </Field>
      )}

      <div className="mt-3 grid grid-cols-[1fr_9rem] gap-3">
        <Field label="البند كما يُطبع">
          <Input
            value={line.label}
            placeholder={line.kind === 'other' ? 'مثال: فتح ملف' : 'يُملأ تلقائياً'}
            onChange={(e) => set({ label: e.target.value, labelTouched: true })}
          />
        </Field>
        <Field label="المبلغ (ج.م)">
          <Input
            type="number"
            min="0"
            dir="ltr"
            value={line.amount}
            onChange={(e) => set({ amount: e.target.value, amountTouched: true })}
          />
        </Field>
      </div>

      {routedWithoutFile && (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-300">
          <Icon name="alert-triangle" size={13} /> التوجيه لقسم يحتاج ملف مريض — سجّل المريض أولاً أو اختر «خدمة أخرى».
        </p>
      )}
    </div>
  );
}

/**
 * Build and issue a receipt. Inline (the visit step of registration) or inside
 * ReceiptDialog. With `requests` (Accounts sent balances to the desk) the lines
 * are those balances and cannot be edited — the server prices them again anyway.
 */
export function ReceiptForm({ patient = null, requests = [], defaults = {}, onIssued, submitLabel }) {
  const { success, error: toastError } = useNotifications();
  const options = useReceiptOptions();
  const settling = requests.length > 0;

  const [receiptType, setReceiptType] = useState(defaults.receiptType || 'cash');
  const [paid, setPaid] = useState('');
  const [paidTouched, setPaidTouched] = useState(false);
  const [method, setMethod] = useState('cash');
  const [partyId, setPartyId] = useState(patient?.contractPartyId ? String(patient.contractPartyId) : '');
  const [patientName, setPatientName] = useState('');
  const [title, setTitle] = useState('');
  const [lines, setLines] = useState(() => (defaults.lines?.length ? defaults.lines : [blankLine(patient ? 'checkup' : 'other')]));
  const [busy, setBusy] = useState(false);

  const fixedLines = requests.map((r) => ({
    label: `${DEPARTMENT_META[r.department]?.label || r.department}: ${r.title}`,
    amount: Number(r.amountDue || 0),
  }));
  const shown = settling ? fixedLines : lines;
  const total = Math.round(shown.reduce((sum, l) => sum + (Number(l.amount) || 0), 0) * 100) / 100;

  // A cash receipt takes the total unless the desk typed something else; a
  // deferred one takes nothing.
  const paidValue = paidTouched ? paid : receiptType === 'cash' || settling ? String(total) : '0';
  const remaining = Math.max(0, Math.round((total - (Number(paidValue) || 0)) * 100) / 100);

  const hasRouted = !settling && lines.some((l) => l.kind !== 'other');
  const incompleteCheckup = !settling && lines.some((l) => l.kind === 'checkup' && !l.doctorId);
  const emptyLine = !settling && lines.some((l) => !String(l.label).trim());
  const blockedByFile = hasRouted && !patient;
  const overpaid = Number(paidValue) > total;

  const issue = async () => {
    setBusy(true);
    try {
      const receipt = await receiptsApi.issue({
        hospitalPatientId: patient?.id || null,
        patientName: patient ? undefined : patientName,
        title: title.trim() || undefined,
        receiptType: settling ? 'cash' : receiptType,
        paidAmount: settling ? undefined : Number(paidValue) || 0,
        paymentMethod: Number(paidValue) > 0 ? method : undefined,
        contractPartyId: partyId || undefined,
        items: settling
          ? fixedLines
          : lines.map((l) => ({
              label: l.label.trim(),
              amount: Number(l.amount) || 0,
              ...(l.kind !== 'other'
                ? {
                    department: l.kind,
                    doctorId: l.doctorId || undefined,
                    clinicId: l.clinicId || undefined,
                    examType: l.kind === 'checkup' ? l.examType : undefined,
                    serviceId: l.serviceId || undefined,
                  }
                : {}),
            })),
        serviceRequestIds: settling ? requests.map((r) => r.id) : undefined,
      });
      success(
        hasRouted ? `صدر الإيصال ${receipt.number} ووُجّه المريض للأقسام` : `صدر الإيصال ${receipt.number}`,
      );
      onIssued?.(receipt);
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (options.error && !options.data) return <ErrorState error={options.error} onRetry={options.reload} />;
  if (!options.data && !settling) return <Spinner label="جارٍ تحميل العيادات والخدمات" variant="inline" />;

  return (
    <div className="space-y-4">
      {!settling && (
        <div className="grid grid-cols-2 gap-3">
          {[
            { id: 'cash', label: 'إيصال نقدي', hint: 'تُحصَّل الآن', icon: 'banknote', tone: 'emerald' },
            { id: 'deferred', label: 'إيصال آجل', hint: 'يُحصَّل لاحقاً', icon: 'hourglass', tone: 'amber' },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              aria-pressed={receiptType === t.id}
              onClick={() => {
                setReceiptType(t.id);
                setPaidTouched(false);
              }}
              className={`type-tile tone-${t.tone} ${receiptType === t.id ? 'type-tile--on' : ''}`}
            >
              <span className="tile3d__face !h-10 !w-10">
                <Icon name={t.icon} size={19} className="tile3d__icon" />
              </span>
              <span className="text-right">
                <span className="block text-sm font-bold text-white">{t.label}</span>
                <span className="block text-[11px] text-slate-400">{t.hint}</span>
              </span>
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {patient ? (
          <Field label="المريض">
            <Input value={patient.fullName} disabled />
          </Field>
        ) : (
          <Field label="اسم المريض" required>
            <Input value={patientName} onChange={(e) => setPatientName(e.target.value)} />
          </Field>
        )}
        <Field label="جهة التعاقد" hint={options.data?.contractParties?.length ? undefined : 'تُضاف الجهات من لوحة التعاقدات'}>
          <Select value={partyId} onChange={(e) => setPartyId(e.target.value)}>
            <option value="">بدون — مريض نقدي</option>
            {(options.data?.contractParties || []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <div>
        <p className="mb-2 text-xs font-bold text-slate-400">الخدمات المطلوبة</p>
        {settling ? (
          <ul className="divide-y divide-white/5 rounded-xl border border-white/5">
            {fixedLines.map((l, i) => (
              <li key={i} className="flex justify-between gap-3 p-2 text-sm text-slate-200">
                <span>{l.label}</span>
                <span className="font-bold">{formatMoney(l.amount)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="space-y-3">
            {lines.map((line) => (
              <LineEditor
                key={line.key}
                line={line}
                options={options.data}
                hasPatient={Boolean(patient)}
                removable={lines.length > 1}
                onChange={(next) => setLines((ls) => ls.map((l) => (l.key === line.key ? next : l)))}
                onRemove={() => setLines((ls) => ls.filter((l) => l.key !== line.key))}
              />
            ))}
            <Button variant="subtle" icon="plus" onClick={() => setLines((ls) => [...ls, blankLine(patient ? 'lab' : 'other')])}>
              خدمة أخرى
            </Button>
          </div>
        )}
      </div>

      <div className="receipt-totals">
        <div>
          <span>الإجمالي</span>
          <b>{formatMoney(total)}</b>
        </div>
        <div>
          <label htmlFor="paid-now">المبلغ المدفوع</label>
          <Input
            id="paid-now"
            type="number"
            min="0"
            dir="ltr"
            value={paidValue}
            disabled={settling}
            onChange={(e) => {
              setPaid(e.target.value);
              setPaidTouched(true);
            }}
            className="!py-2"
          />
        </div>
        <div className={remaining > 0 ? 'text-amber-200' : ''}>
          <span>المتبقي</span>
          <b>{formatMoney(remaining)}</b>
        </div>
        <div>
          <label htmlFor="pay-method">طريقة الدفع</label>
          <Select id="pay-method" value={method} onChange={(e) => setMethod(e.target.value)} className="!py-2" disabled={!(Number(paidValue) > 0)}>
            {Object.entries(METHOD_LABELS).map(([id, label]) => (
              <option key={id} value={id}>
                {label}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <details className="rounded-xl border border-white/5 p-3 text-sm text-slate-300">
        <summary className="cursor-pointer text-xs font-bold text-slate-400">عنوان مختلف للإيصال (اختياري)</summary>
        <Input
          className="mt-2"
          placeholder={TITLE_FOR_TYPE[settling ? 'cash' : receiptType]}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </details>

      <p className="rounded-xl bg-cyan-500/10 px-3 py-2 text-xs text-cyan-200">
        <Icon name="info" size={13} className="inline" /> رقم الإيصال والباركود والتاريخ والوقت والتوقيع تُضاف تلقائياً عند الإصدار.
        {hasRouted && ' سيظهر المريض فوراً عند الأقسام التي اخترتها.'}
      </p>

      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-bold text-white">الإجمالي: {formatMoney(total)}</span>
        <Button
          icon="receipt"
          loading={busy}
          disabled={
            blockedByFile ||
            incompleteCheckup ||
            emptyLine ||
            overpaid ||
            (!patient && !settling && !patientName.trim())
          }
          onClick={issue}
        >
          {submitLabel || (settling ? 'تحصيل وإصدار الإيصال' : hasRouted ? 'إصدار الإيصال وتوجيه المريض' : 'إصدار الإيصال')}
        </Button>
      </div>
    </div>
  );
}

/** The form in a modal; the preview once issued. */
export function ReceiptDialog({ open, onClose, patient = null, requests = [], defaults = {}, onIssued, afterIssue }) {
  const [issued, setIssued] = useState(null);
  const close = () => {
    setIssued(null);
    onClose?.();
  };
  return (
    <Modal
      open={open}
      onClose={close}
      title={issued ? `الإيصال ${issued.number}` : 'إصدار إيصال إلكتروني'}
      icon="receipt"
      size="lg"
      closeOnBackdrop={!issued}
      footer={
        issued ? (
          <div className="flex justify-end">
            <Button variant="ghost" onClick={close}>
              تم
            </Button>
          </div>
        ) : null
      }
    >
      {issued ? (
        <ReceiptPreview receipt={issued} extra={afterIssue ? afterIssue(issued) : null} />
      ) : (
        <ReceiptForm
          patient={patient}
          requests={requests}
          defaults={defaults}
          onIssued={(receipt) => {
            setIssued(receipt);
            onIssued?.(receipt);
          }}
        />
      )}
    </Modal>
  );
}

/** A receipt fetched by id — for reprinting from a list. */
export function ReceiptById({ id }) {
  const [state, setState] = useState({ receipt: null, error: null });
  const load = () => {
    setState({ receipt: null, error: null });
    receiptsApi
      .get(id)
      .then((receipt) => setState({ receipt, error: null }))
      .catch((error) => setState({ receipt: null, error }));
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  if (state.error) return <ErrorState error={state.error} onRetry={load} />;
  if (!state.receipt) return <Spinner label="جارٍ تحميل الإيصال" variant="inline" />;
  return <ReceiptPreview receipt={state.receipt} />;
}

/** Summarise a receipt's routed lines, for the desk's lists. */
export function useRoutedSummary(receipt) {
  return useMemo(
    () => (receipt?.items || []).filter((i) => i.department).map((i) => DEPARTMENT_META[i.department]?.label || i.department),
    [receipt],
  );
}

export default ReceiptDialog;
